import { Checkbox, IconButton, Stack, Tooltip, useMediaQuery, useTheme } from '@mui/material'
import { CheckboxField } from '@e3dc-react/shell/Elements/InputFields'
import { AccountLinkModel, InstallationInvitationModel } from 'src/Libs/installation-rights-management/models'
import React, { ReactNode, useCallback, useContext, useMemo, useState } from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { installationInvitationsApi, invitationsApi } from 'src/Libs/installation-rights-management/api'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import ToggleWriteAccessControl from 'src/Libs/installation-rights-management/components/user-access-and-invitations/ToggleWriteAccessControl'
import { K1Information, J2Editacontent } from '@e3dc-react/icons'
import {
  InvitationActionMenu,
  InvitationNameColumn,
  InvitationStatusIndicatorColumn,
  InvitationTypeColumn,
} from 'src/Libs/installation-rights-management/components/user-access-and-invitations/invitation'
import {
  AccountLinkActionMenu,
  AccountLinkNameColumn,
  AccountLinkStatusIndicatorColumn,
  AccountLinkTypeColumn,
} from 'src/Libs/installation-rights-management/components/user-access-and-invitations/account-link'
import useModals from 'src/Hooks/useModals'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useAPIAccessChecker, useUserAccessAndInvitations } from 'src/Libs/installation-rights-management/hooks/index'
import { Installation } from 'src/Libs/installations/models/installation.model'
import { AccountLinksContext } from '../provider/AccountLinksContext'
import { useTypedSelector } from 'src/State/RootReducer'
import { SERVICE_LEVELS_ALL } from 'src/Configuration'
import { fetchUsersWithConnections } from 'src/Libs/user/api/user.api'
import { isDirektMarketingAccount, isInstallationOwner } from '../utils'
import { ApiPermissionType } from 'src/Libs/api/types'

interface HeaderData {
  label: string
  key: string
  tooltip?: string
}

interface UseInstallationAccessTableHook {
  /**
   * Load / reload all available AccountLink entries for an initial provided Installation-reference.
   * It also triggers the re-generation of the entries from `header`, `data` and `actionMenus`
   */
  refreshData: () => Promise<void>
  /**
   * Information for the head of an DefaultTable-component
   */
  header: HeaderData[]
  /**
   * A list of pre-configured ReactNode items to be displayed in a DefaultTable component.
   * It contains a 2-multilevel list which represents the rows and columns of a table.
   */
  data: React.ReactNode[][]

  /**
   * All the relevant action menus needed for each single row provided in the `data`-prop, to be used in a DefaultTable component.
   */
  actionMenus: React.ReactNode[]

  /**
   * A responsive flag that toggles as soon as the defined breakpoint for a smaller view is hit
   */
  isShortTable: boolean

  /**
   * The current loaded AccountLink entities, accumulated in as nodes in the `data`-property.
   */
  accountLinks: AccountLinkModel[]

  /**
   * The current loaded InstallationInvitation entities, accumulated in as nodes in the `data`-property.
   */
  invitations: InstallationInvitationModel[]
}

/**
 * Providing the table configuration and management functions for the access management table (AccountLink models) of this module.
 *
 * This hook is intended to be used as a support to populate a DefaultTable component.
 *
 * @param installation
 */
const useInstallationAccessTable = (installation: Installation): UseInstallationAccessTableHook => {
  const { t } = useCustomTranslation()
  const { openInstallationAccessSettings } = useModals()
  const { removeAccountFromAccess } = useUserAccessAndInvitations()
  const { breakpoints } = useTheme()
  const isShortTable = useMediaQuery(breakpoints.down('sm'))
  const [data, setData] = useState<ReactNode[][]>([])
  const { accountLinks, updateAccountLinks } = useContext(AccountLinksContext)
  const [invitations, setInvitations] = useState<InstallationInvitationModel[]>([])
  const authFetch = useAuthFetchWithResponse()
  const { enqueueSuccessStack, enqueueErrorStack } = useSnackbar()
  const userLevel = useTypedSelector(state => state.user.userData?.level)
  const filteredAccountLinks = accountLinks.filter(item => item.permissionType !== ApiPermissionType.noAccess)

  const { isApiAccessFeatureEnabled, isUserLevelAllowedToGetApiAccess } = useAPIAccessChecker(installation)

  const resendInvitation = useCallback(
    (invitation: InstallationInvitationModel): void => {
      invitationsApi
        .resendInvitation(authFetch, invitation.token, installation.id.toString())
        .then(() => enqueueSuccessStack(t('installation.access-and-invitations.resend.success')))
        .catch(error => {
          console.error(error)
          enqueueErrorStack(t('installation.access-and-invitations.resend.failed'))
        })
    },
    [authFetch, enqueueErrorStack, enqueueSuccessStack, t, installation.id]
  )

  const accountLinkToRowData = useCallback(
    (item: AccountLinkModel): React.ReactNode[] => {
      const index = accountLinks.findIndex(acountLink => acountLink.id === item.id)
      const isAccessBlocked = item?.permissionType === ApiPermissionType.blocked
      const isAccessPending = item?.permissionType === ApiPermissionType.pending
      const isAccessPendingOrBlocked = isAccessPending || isAccessBlocked
      let key = 0
      return [
        <AccountLinkNameColumn key={key++} item={item} />,
        <AccountLinkTypeColumn key={key++} item={item} />,
        <AccountLinkStatusIndicatorColumn key={key++} item={item} withLabel={!isShortTable} />,
        isAccessPendingOrBlocked ? null : <Checkbox disabled checked key={key++} />,
        isAccessPendingOrBlocked ? null : (
          <ToggleWriteAccessControl key={key++} item={item} installation={installation} name={`items.${index}.hasWriteAccess`} />
        ),
        isAccessPendingOrBlocked || !isUserLevelAllowedToGetApiAccess(item) ? null : (
          <ToggleWriteAccessControl key={key++} item={item} installation={installation} name={`items.${index}.hasHagerEnergyApiAccess`} />
        ),
      ]
    },
    [installation, accountLinks, isUserLevelAllowedToGetApiAccess, isShortTable]
  )
  const invitationsToRowData = useCallback(
    (item: InstallationInvitationModel): React.ReactNode[] => {
      let key = 0
      return [
        <InvitationNameColumn key={key++} item={item} />,
        <InvitationTypeColumn key={key++} item={item} />,
        <InvitationStatusIndicatorColumn key={key++} item={item} onConfirm={resendInvitation} />,
        <Checkbox disabled key={key++} />,
        <CheckboxField disabled key={key++} />,
        isApiAccessFeatureEnabled ? <CheckboxField disabled key={key++} /> : null,
      ]
    },
    [isApiAccessFeatureEnabled, resendInvitation]
  )

  const refreshData = useCallback(async (): Promise<void> => {
    if (!installation) {
      console.error('Invalid installation', installation)
      return
    }

    const usersAccountsIds = accountLinks.filter(elem => elem.accountType === 'user').map(elem => elem.accountId)

    const usersWithEmailsConnected =
      usersAccountsIds.length > 0
        ? await fetchUsersWithConnections(
            {
              fields: 'user.ID,location.Email',
              filterOr: `user.ID=IN(${usersAccountsIds})`,
              connect: 'location',
            },
            authFetch
          )
        : {
            value: [],
          }

    const emailByUserId = Object.fromEntries(usersWithEmailsConnected.value?.map(elem => [elem['user.ID'], elem['location.Email']]) ?? [])

    const accountLinksWithEmails = filteredAccountLinks.map(elem => ({
      ...elem,
      email: emailByUserId?.[elem.accountId] ?? '',
    }))

    const accountLinksRowData = accountLinksWithEmails.map(accountLinkToRowData)

    const invitationApiResult = await installationInvitationsApi.findAll(installation.id, authFetch)
    const invitationsRowData = (invitationApiResult.value || []).map(invitationsToRowData)

    const rowData = [...accountLinksRowData, ...invitationsRowData]
    setData(rowData)

    if (invitationApiResult.value) {
      setInvitations(invitationApiResult.value)
    }
  }, [installation, accountLinks, authFetch, filteredAccountLinks, accountLinkToRowData, invitationsToRowData])

  const handleSettingsDialogBeforeClose = useCallback((): void => {
    updateAccountLinks?.()
    refreshData().catch(console.error)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshData])

  const openSettingsDialog = useCallback(
    (value: AccountLinkModel, installation: Installation): void => {
      openInstallationAccessSettings({ value, installation, afterDataUpdate: handleSettingsDialogBeforeClose })
    },
    [handleSettingsDialogBeforeClose, openInstallationAccessSettings]
  )

  const removeAccessForAccount = useCallback(
    (value: AccountLinkModel): void => {
      removeAccountFromAccess(installation.id, value.id)
        .then(() => {
          updateAccountLinks?.()
          refreshData().catch(console.error)
          enqueueSuccessStack(t('installation.access-and-invitations.remove-access.success'))
        })
        .catch(() => enqueueErrorStack(t('installation.access-and-invitations.remove-access.failed')))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enqueueErrorStack, enqueueSuccessStack, installation.id, refreshData, removeAccountFromAccess, t]
  )

  const accountLinkToActions = useCallback(
    (item: AccountLinkModel, index: number): React.ReactNode => {
      if (isInstallationOwner(item, installation) || (isDirektMarketingAccount(item) && !SERVICE_LEVELS_ALL.includes(userLevel || 'USER'))) {
        return (
          <Stack direction={'row'} justifyContent={'center'} alignItems={'flex-start'} key={`info-${index}`}>
            <Tooltip
              title={
                isDirektMarketingAccount(item)
                  ? t('installation.access-and-invitations.no-action-for-direct-marketing')
                  : t('installation.access-and-invitations.no-action-for-owner')
              }
              placement="bottom-start"
            >
              <i>
                <K1Information />
              </i>
            </Tooltip>
          </Stack>
        )
      }
      const actions = []

      if (isShortTable) {
        actions.push(
          <IconButton onClick={() => openSettingsDialog(item, installation)} key={`edit-${index}`}>
            <J2Editacontent />
          </IconButton>
        )
      }

      actions.push(<AccountLinkActionMenu item={item} onRemove={removeAccessForAccount} key={`menu-${index}`} />)

      return (
        <Stack direction={'row'} justifyContent={'center'} alignItems={'flex-start'} key={`action-stack-${index}`}>
          {actions}
        </Stack>
      )
    },
    [userLevel, installation, isShortTable, openSettingsDialog, removeAccessForAccount, t]
  )

  const invitationsToActions = useCallback(
    (item: InstallationInvitationModel, index: number): React.ReactNode => {
      return (
        <Stack direction={'row'} justifyContent={'center'} alignItems={'flex-start'} key={index}>
          <InvitationActionMenu item={item} onChange={refreshData} onResendInvite={isShortTable && !item.expiredAt ? resendInvitation : undefined} />
        </Stack>
      )
    },
    [isShortTable, refreshData, resendInvitation]
  )

  const tableHeader = useMemo<HeaderData[]>(() => {
    const headerData = [
      'installation.access-and-invitations.table.header.columns.user',
      'installation.access-and-invitations.table.header.columns.type',
      'installation.access-and-invitations.table.header.columns.status',
      'installation.access-and-invitations.table.header.columns.can-read',
      'installation.access-and-invitations.table.header.columns.can-write',
    ].map(elem => ({ label: t(elem), key: elem } as HeaderData))

    if (isApiAccessFeatureEnabled) {
      const key = 'installation.access-and-invitations.table.header.columns.api-access'
      headerData.push({
        label: t(key),
        key: key,
        tooltip: t(key + '.description'),
      })
    } else {
      // Action-Data
      headerData.push({ label: '', key: '' })
    }

    if (isShortTable) {
      return headerData.slice(0, 1)
    }

    return headerData
  }, [isApiAccessFeatureEnabled, isShortTable, t])

  const tableBody = useMemo<React.ReactNode[][]>(() => {
    if (isShortTable) {
      return data.map(row => row.slice(0, 1))
    }

    return data
  }, [isShortTable, data])

  const actionMenus = useMemo<React.ReactNode[]>(() => {
    const accountLinkActions = filteredAccountLinks.map(accountLinkToActions)
    const invitationActions = invitations.map(invitationsToActions)
    return [...accountLinkActions, ...invitationActions]
  }, [filteredAccountLinks, accountLinkToActions, invitations, invitationsToActions])

  return {
    refreshData,
    header: tableHeader,
    data: tableBody,
    actionMenus,
    isShortTable,
    accountLinks,
    invitations,
  }
}

export default useInstallationAccessTable
